import React, {
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Divider, Flex, List, Spinner } from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { marketRankingFilterAtom } from './states/marketRankingFilter.atom';
import { useMarketRankingTypes } from './queries/useMarketRankingTypes';
import { useMarketRankings } from './queries/useMarketRankings';
import { MarketRankingCard } from './components/MarketRankingCard';
import {
  MarketTypeHeader,
  MarketRankingFilterChips,
} from './components/MarketRankingListHeader';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import { useOpenDeeplink } from 'shared/src/hooks';
import { bridge, isNotNil } from 'shared/src/utils';
import type { MarketRankingItem } from 'shared/src/api';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

type ListItem =
  | { type: 'filter' }
  | { type: 'market'; data: MarketRankingItem };

const MarketRankingContent = () => {
  const [filter] = useAtom(marketRankingFilterAtom);
  const { data: typesData } = useMarketRankingTypes();
  const homeScroll = useContext(HomeScrollContext);
  const { openDeeplink } = useOpenDeeplink();

  const selectedMarketType =
    filter.selectedMarketType ?? typesData.market_types[0] ?? null;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [favoriteMap, setFavoriteMap] = useState<{ [key: number]: boolean }>(
    {},
  );

  const { data, fetchNextPage, hasNextPage, refetch } = useMarketRankings({
    market_type_sno: selectedMarketType?.sno ?? 0,
    category_sno: filter.selectedCategorySno ?? undefined,
    age_list:
      filter.selectedAgeValues.length > 0
        ? filter.selectedAgeValues.filter((v): v is number => v !== null)
        : undefined,
    style_list:
      filter.selectedStyles.length > 0 ? filter.selectedStyles : undefined,
  });

  const listData = useMemo<ListItem[]>(
    () => [
      { type: 'filter' },
      ...(data?.markets ?? []).map(m => ({
        type: 'market' as const,
        data: m,
      })),
    ],
    [data?.markets],
  );

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      homeScroll?.onScrollY(e.nativeEvent.contentOffset.y);
    },
    [homeScroll],
  );

  const handlePressMarket = useCallback(
    (item: MarketRankingItem) => {
      if (isNotNil(item.deeplink)) {
        openDeeplink(item.deeplink);
      }
    },
    [openDeeplink],
  );

  const handlePressFavorite = useCallback(
    (item: MarketRankingItem) => {
      const isFavorite = favoriteMap[item.sno] ?? isNotNil(item.favorite);
      setFavoriteMap(prev => ({ ...prev, [item.sno]: !isFavorite }));
      bridge.favoriteMarket({
        market_sno: item.sno,
        is_favorite: !isFavorite,
      });
    },
    [favoriteMap],
  );

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === 'filter') {
        return (
          <Suspense fallback={<Spinner />}>
            <MarketRankingFilterChips
              marketTypeSno={selectedMarketType?.sno ?? 0}
            />
          </Suspense>
        );
      }

      const market = item.data;
      return (
        <MarketRankingCard
          item={market}
          isFavorite={favoriteMap[market.sno] ?? isNotNil(market.favorite)}
          onPress={() => handlePressMarket(market)}
          onPressFavorite={() => handlePressFavorite(market)}
        />
      );
    },
    [
      selectedMarketType?.sno,
      favoriteMap,
      handlePressMarket,
      handlePressFavorite,
    ],
  );

  return (
    <List<ListItem>
      data={listData}
      renderItem={renderItem}
      keyExtractor={item =>
        item.type === 'filter' ? 'filter' : `market-${item.data.sno}`
      }
      stickyHeaderIndices={[1]}
      ItemSeparatorComponent={() => <Divider size="small" />}
      ListHeaderComponent={<MarketTypeHeader />}
      refreshing={isRefreshing}
      onRefresh={() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
      }}
      onEndReached={() => {
        if (hasNextPage) {
          fetchNextPage();
        }
      }}
      onEndReachedThreshold={1}
      onScroll={handleScroll}
      scrollEventThrottle={16}
    />
  );
};
export const MarketRanking = () => {
  return (
    <React.Suspense
      fallback={
        <Flex.Center>
          <Spinner />
        </Flex.Center>
      }
    >
      <MarketRankingContent />
    </React.Suspense>
  );
};
